import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { CommonTheme, useCurrentTheme } from 'app/shared/theme';
import { ImageSourceMap } from 'app/shared/typings';
import {
  withBorderCSS,
  WithBorderProps,
  withDimensionsCSS,
  WithDimensionsProps,
  withDisplayCSS,
  WithDisplayProps,
  withShadowCSS,
  WithShadowProps,
  withSpacingCSS,
  WithSpacingProps,
} from 'app/shared/components/styleUtils';

interface PictureProps
  extends WithSpacingProps,
    WithDimensionsProps,
    WithDisplayProps {}

interface ImgProps
  extends WithBorderProps,
    WithShadowProps,
    WithDimensionsProps {
  grayscale?: boolean;
}

export interface ImageProps extends PictureProps, ImgProps {
  src: string;
  srcMobile?: string;
  srcTablet?: string;
  srcSmallDesktop?: string;
  srcLargeDesktop?: string;
  alt?: string;
  animate?: boolean;
  animateOnHover?: boolean;
  animateOnTouchScreen?: boolean;
  dataSourceMap?: ImageSourceMap;
}

interface PictureCSSProps extends PictureProps {
  theme: CommonTheme;
}
const StyledPicture = styled.picture<PictureCSSProps>`
  ${withSpacingCSS};
  ${withDimensionsCSS};
  ${withDisplayCSS};
`;

const StyledImage = styled.img<ImgProps>`
  object-fit: cover;
  object-position: top;

  ${({ grayscale, h = 'inherit', w = '100%' }) => css`
    ${withDimensionsCSS({ h, w })};
    ${grayscale && 'filter: grayscale(100%);'}
  `}

  ${withBorderCSS};
  ${withShadowCSS};
`;

const StyledSource = styled.source`
  display: none;
`;

export const Image = ({
  src,
  srcMobile,
  srcTablet,
  srcSmallDesktop,
  srcLargeDesktop,
  dataSourceMap,
  grayscale = false,
  b,
  bt,
  br,
  bb,
  bl,
  bw,
  borderRadius,
  borderRadiusBottomLeft,
  borderRadiusBottomRight,
  borderRadiusTopLeft,
  borderRadiusTopRight,
  boxShadow,
  alt,
  h,
  w,
  animate = false,
  animateOnHover = false,
  animateOnTouchScreen = false,
  ...props
}: ImageProps) => {
  const { breakPoints } = useCurrentTheme() as CommonTheme;

  const [isHovering, setIsHovering] = useState(false);

  const deviceHasPointer = window.matchMedia('(pointer: fine)');

  function formatUrlForDisplay(url: string, dataUrl?: string) {
    const isTouchScreen = !deviceHasPointer.matches;

    if (
      animate ||
      (animateOnHover && isHovering) ||
      (animateOnTouchScreen && isTouchScreen)
    ) {
      return dataUrl || url;
    }

    return url;
  }

  const {
    src: dataSrc,
    srcMobile: dataSrcMobile,
    srcTablet: dataSrcTablet,
    srcSmallDesktop: dataSrcSmallDesktop,
    srcLargeDesktop: dataSrcLargeDesktop,
  } = dataSourceMap || {};

  return (
    <StyledPicture
      {...props}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <StyledSource
        srcSet={formatUrlForDisplay(
          srcLargeDesktop || src,
          dataSrcLargeDesktop || dataSrc
        )}
        media={`(min-width: ${breakPoints.largeDesktop}px)`}
      />
      <StyledSource
        srcSet={formatUrlForDisplay(
          srcSmallDesktop || src,
          dataSrcSmallDesktop || dataSrc
        )}
        media={`(min-width: ${breakPoints.smallDesktop}px)`}
      />
      <StyledSource
        srcSet={formatUrlForDisplay(srcTablet || src, dataSrcTablet || dataSrc)}
        media={`(min-width: ${breakPoints.tablet}px)`}
      />
      <StyledSource
        srcSet={formatUrlForDisplay(srcMobile || src, dataSrcMobile || dataSrc)}
        media={`(min-width: ${breakPoints.mobile}px)`}
      />
      <StyledImage
        src={formatUrlForDisplay(src, dataSrc)}
        data-src={dataSrc}
        alt={alt}
        sizes="100vw"
        grayscale={grayscale}
        b={b}
        bt={bt}
        br={br}
        bb={bb}
        bl={bl}
        borderRadius={borderRadius}
        bw={bw}
        borderRadiusBottomLeft={borderRadiusBottomLeft}
        borderRadiusBottomRight={borderRadiusBottomRight}
        borderRadiusTopLeft={borderRadiusTopLeft}
        borderRadiusTopRight={borderRadiusTopRight}
        boxShadow={boxShadow}
        h={h}
        w={w}
      />
    </StyledPicture>
  );
};
